import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
interface NavbarProps {
  onNavigateHome: () => void;
}
export function Navbar({
  onNavigateHome
}: NavbarProps) {
  const {
    t
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navLinks = [{
    labelKey: 'nav.vehicles',
    href: '#vozy'
  }, {
    labelKey: 'nav.about',
    href: '#o-nas'
  }, {
    labelKey: 'nav.services',
    href: '#sluzby'
  }, {
    labelKey: 'nav.contact',
    href: '#kontakt'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    // First navigate to home if not already there
    if (!window.location.hash.startsWith('#/') || window.location.hash === '#/') {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      onNavigateHome();
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };
  const handleLogoClick = () => {
    setIsMobileMenuOpen(false);
    onNavigateHome();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <>
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-md border-b border-[#2a2a2a]' : 'bg-transparent'}`} data-id="element-118">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-119">
          <div className="flex items-center justify-between h-20" data-id="element-120">
            {/* Logo */}
            <button onClick={handleLogoClick} className="flex-shrink-0" data-id="element-121">
              <img src="https://cdn.magicpatterns.com/uploads/jWkL4ZwbKDHse1C73JXje9/logoVector.svg" alt="Porsche Top" className="h-8 w-auto brightness-0 invert" data-id="element-122" />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8" data-id="element-123">
              {navLinks.map(link => <button key={link.href} onClick={() => handleNavClick(link.href)} className="text-[#a3a3a3] hover:text-white transition-colors duration-300 text-sm font-medium tracking-wide" data-id="element-124">
                  {t(link.labelKey)}
                </button>)}
              <LanguageSwitcher data-id="element-125" />
              <button onClick={() => handleNavClick('#kontakt')} className="bg-[#c8a96e] hover:bg-[#d4b87a] text-[#0a0a0a] px-6 py-2.5 text-sm font-semibold tracking-wide transition-colors duration-300" data-id="element-126">
                {t('nav.contactBtn')}
              </button>
            </div>

            {/* Mobile: Language Switcher + Menu Button */}
            <div className="md:hidden flex items-center gap-4" data-id="element-127">
              <LanguageSwitcher data-id="element-128" />
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-white" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-129">
                {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-130" /> : <MenuIcon className="w-6 h-6" data-id="element-131" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-132">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-40 bg-[#0a0a0a] pt-20 md:hidden" data-id="element-133">
            <div className="flex flex-col items-center justify-center h-full space-y-8" data-id="element-134">
              {navLinks.map((link, index) => <motion.button key={link.href} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} onClick={() => handleNavClick(link.href)} className="text-2xl font-medium text-white hover:text-[#c8a96e] transition-colors" data-id="element-135">
                  {t(link.labelKey)}
                </motion.button>)}
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} onClick={() => handleNavClick('#kontakt')} className="mt-4 bg-[#c8a96e] hover:bg-[#d4b87a] text-[#0a0a0a] px-8 py-3 text-lg font-semibold transition-colors" data-id="element-136">
                {t('nav.contactBtn')}
              </motion.button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}